import { useCallback, useEffect, useState } from 'react';
import { toast } from 'sonner';
import { motion } from 'framer-motion';
import { CheckCircle2, XCircle, Loader2, RotateCcw, ArrowRight, ClipboardCheck, Award } from 'lucide-react';
import { academyApi } from '@/services/academyApi';

/*
  Quiz tipo test de FHC Academy (un bloque o la evaluación final).
  - El servidor envía las preguntas sin la respuesta correcta y corrige él.
  - El orden de las respuestas se baraja en cada intento (se envía el índice original).
  - Si suspendes, solo se marca qué preguntas fallaste; si apruebas, ves la solución y la explicación.
*/

const LETTERS = ['A', 'B', 'C', 'D', 'E', 'F'];

function shuffled(n) {
  const a = [...Array(n).keys()];
  for (let i = a.length - 1; i > 0; i -= 1) {
    const j = Math.floor(Math.random() * (i + 1));
    [a[i], a[j]] = [a[j], a[i]];
  }
  return a;
}

export default function QuizPanel({ courseId, quizId, isFinal, onPassed, onNext, nextLabel }) {
  const [quiz, setQuiz] = useState(null);
  const [error, setError] = useState(null);
  const [orders, setOrders] = useState({});
  const [answers, setAnswers] = useState({});
  const [result, setResult] = useState(null);
  const [sending, setSending] = useState(false);

  const reshuffle = useCallback((qs) => {
    setOrders(Object.fromEntries(qs.map((q) => [q.id, shuffled(q.options.length)])));
  }, []);

  useEffect(() => {
    setQuiz(null); setError(null); setResult(null); setAnswers({});
    academyApi.quiz(courseId, quizId)
      .then((q) => { setQuiz(q); reshuffle(q.questions); })
      .catch((e) => setError(e.message));
  }, [courseId, quizId, reshuffle]);

  const retry = () => {
    setResult(null); setAnswers({}); reshuffle(quiz.questions);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const submit = async () => {
    setSending(true);
    try {
      const r = await academyApi.submitQuiz(courseId, quizId, answers);
      setResult(r);
      if (r.passed) {
        toast.success(isFinal ? 'Evaluación final aprobada' : 'Quiz aprobado');
        onPassed?.(r);
      } else {
        toast(`Has acertado ${r.score} de ${r.total}. Repasa y vuelve a intentarlo.`);
      }
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } catch (e) {
      toast.error(e.message);
    } finally {
      setSending(false);
    }
  };

  if (error) {
    return <div className="rounded-2xl border border-white/[0.06] bg-white/[0.02] p-6 text-sm text-gray-300" data-testid="quiz-error">{error}</div>;
  }
  if (!quiz) {
    return <div className="flex justify-center py-16"><Loader2 className="animate-spin text-[#c9a84c]" size={22} /></div>;
  }

  const byId = Object.fromEntries((result?.results || []).map((r) => [r.id, r]));
  const answered = quiz.questions.filter((q) => Number.isInteger(answers[q.id])).length;
  const need = Math.ceil((quiz.pass_pct / 100) * quiz.questions.length);

  return (
    <div className="space-y-4" data-testid="quiz-panel">
      <div className="rounded-2xl border border-[#c9a84c]/20 bg-[#c9a84c]/[0.04] p-5">
        <div className="flex items-start gap-3">
          <span className="w-10 h-10 flex-shrink-0 rounded-full bg-[#c9a84c]/15 flex items-center justify-center">
            {isFinal ? <Award size={18} className="text-[#c9a84c]" /> : <ClipboardCheck size={18} className="text-[#c9a84c]" />}
          </span>
          <div className="min-w-0">
            <p className="text-[11px] uppercase tracking-wider font-semibold text-[#c9a84c]">{isFinal ? 'Evaluación final' : 'Quiz del bloque'}</p>
            <h3 className="text-white font-semibold text-lg leading-snug">{quiz.title}</h3>
            <p className="text-sm text-gray-400 mt-1">
              {quiz.questions.length} preguntas · apruebas con {need} aciertos ({quiz.pass_pct} %) · puedes repetirlo las veces que necesites
              {isFinal ? '. Al aprobarlo recibes tu diploma.' : '. Al aprobarlo se abre el siguiente bloque.'}
            </p>
          </div>
        </div>
      </div>

      {result && (
        <motion.div initial={{ opacity: 0, y: 6 }} animate={{ opacity: 1, y: 0 }}
          className={`rounded-2xl border p-5 ${result.passed ? 'border-emerald-500/30 bg-emerald-500/[0.07]' : 'border-amber-400/30 bg-amber-400/[0.06]'}`}
          data-testid={result.passed ? 'quiz-passed' : 'quiz-failed'}>
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
            <div>
              <p className={`font-semibold ${result.passed ? 'text-emerald-300' : 'text-amber-200'}`}>
                {result.passed ? '¡Aprobado!' : 'Todavía no'} · {result.score} de {result.total} ({result.pct} %)
              </p>
              <p className="text-sm text-gray-300 mt-1">
                {result.passed
                  ? 'Abajo tienes la solución comentada de cada pregunta.'
                  : `Necesitas ${need} aciertos. Te marcamos las preguntas que fallaste: repasa esas clases y vuelve a intentarlo.`}
              </p>
            </div>
            <div className="flex gap-2">
              {!result.passed && (
                <button type="button" onClick={retry} data-testid="quiz-retry"
                  className="inline-flex items-center gap-2 rounded-xl bg-[#c9a84c] px-4 py-2.5 text-sm font-semibold text-[#0a1628] hover:bg-[#d6b75a]">
                  <RotateCcw size={15} /> Volver a intentarlo
                </button>
              )}
              {result.passed && onNext && (
                <button type="button" onClick={onNext} data-testid="quiz-next"
                  className="inline-flex items-center gap-2 rounded-xl bg-[#c9a84c] px-4 py-2.5 text-sm font-semibold text-[#0a1628] hover:bg-[#d6b75a]">
                  {nextLabel} <ArrowRight size={15} />
                </button>
              )}
            </div>
          </div>
        </motion.div>
      )}

      <ol className="space-y-3">
        {quiz.questions.map((q, qi) => {
          const res = byId[q.id];
          const order = orders[q.id] || q.options.map((_, i) => i);
          return (
            <li key={q.id} className={`rounded-2xl border p-4 sm:p-5 ${res ? (res.correct ? 'border-emerald-500/25' : 'border-red-400/30') : 'border-white/[0.06]'} bg-white/[0.02]`}
              data-testid={`quiz-q-${qi}`}>
              <div className="flex items-start gap-2">
                <span className="text-xs font-semibold text-[#c9a84c] tabular-nums mt-0.5">{qi + 1}.</span>
                <p className="flex-1 text-sm sm:text-[15px] text-white leading-relaxed">{q.text}</p>
                {res && (res.correct ? <CheckCircle2 size={18} className="text-emerald-400 flex-shrink-0" /> : <XCircle size={18} className="text-red-400 flex-shrink-0" />)}
              </div>
              <div className="mt-3 grid gap-2" role="radiogroup" aria-label={`Pregunta ${qi + 1}`}>
                {order.map((oi, pos) => {
                  const selected = answers[q.id] === oi;
                  const isCorrect = res?.correct_option === oi;
                  let cls = 'border-white/[0.08] hover:border-[#c9a84c]/30 text-gray-300';
                  if (selected) cls = 'border-[#c9a84c]/60 bg-[#c9a84c]/10 text-white';
                  if (result?.passed && isCorrect) cls = 'border-emerald-500/50 bg-emerald-500/10 text-white';
                  else if (result && selected && !res?.correct) cls = 'border-red-400/50 bg-red-500/10 text-white';
                  return (
                    <button key={oi} type="button" role="radio" aria-checked={selected} disabled={!!result}
                      onClick={() => setAnswers((a) => ({ ...a, [q.id]: oi }))}
                      className={`flex items-start gap-3 rounded-xl border px-3 py-2.5 text-left text-sm transition-colors disabled:cursor-default ${cls}`}>
                      <span className={`mt-0.5 flex h-5 w-5 flex-shrink-0 items-center justify-center rounded-full border text-[10px] font-semibold ${selected ? 'border-[#c9a84c] text-[#c9a84c]' : 'border-white/20 text-gray-400'}`}>
                        {LETTERS[pos]}
                      </span>
                      <span className="leading-relaxed">{q.options[oi]}</span>
                    </button>
                  );
                })}
              </div>
              {result?.passed && res?.explanation && (
                <p className="mt-3 rounded-xl bg-white/[0.03] px-3 py-2.5 text-[13px] leading-relaxed text-gray-300">
                  <span className="font-semibold text-[#c9a84c]">Por qué: </span>{res.explanation}
                </p>
              )}
            </li>
          );
        })}
      </ol>

      {!result && (
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 rounded-2xl border border-white/[0.06] bg-white/[0.02] p-4">
          <p className="text-sm text-gray-400">{answered} de {quiz.questions.length} respondidas</p>
          <button type="button" onClick={submit} disabled={sending || answered < quiz.questions.length} data-testid="quiz-submit"
            className="inline-flex items-center justify-center gap-2 rounded-xl bg-[#c9a84c] px-5 py-2.5 text-sm font-semibold text-[#0a1628] hover:bg-[#d6b75a] disabled:opacity-40">
            {sending && <Loader2 size={15} className="animate-spin" />} Corregir
          </button>
        </div>
      )}
    </div>
  );
}
